import { api } from './api';
import type { ApiResponse } from '../types';
import type { SurveyConfig } from '../types/survey';

export interface ItineraryItem {
  day: string;
  time: string;
  activity: string;
  location: string;
  description: string;
  icon: string;
  isNew?: boolean;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface Event {
  id: string;
  familyId: string;
  slug?: string | null;
  title: string;
  description?: string | null;
  subtitle?: string | null;
  motto?: string | null;
  yearsMilestone?: string | null;
  startDate: string;
  endDate?: string | null;
  location?: string | null;
  venue?: string | null;
  address?: string | null;
  hotelUrl?: string | null;
  rsvpDeadline?: string | null;
  itinerary?: ItineraryItem[] | null;
  faq?: FaqItem[] | null;
  surveyConfig?: SurveyConfig | null;
  published?: boolean | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateEventInput {
  title?: string;
  description?: string | null;
  subtitle?: string | null;
  motto?: string | null;
  yearsMilestone?: string | null;
  startDate?: string;
  endDate?: string | null;
  location?: string | null;
  venue?: string | null;
  address?: string | null;
  hotelUrl?: string | null;
  rsvpDeadline?: string | null;
  itinerary?: ItineraryItem[] | null;
  faq?: FaqItem[] | null;
  published?: boolean;
}

export interface GetUpcomingEventsParams {
  limit?: number;
  familyId?: string;
}

export interface UpcomingEventsResponse {
  events: Event[];
  count: number;
}

export interface EventResponse {
  event: Event;
}

export interface EventsListResponse {
  events: Event[];
  count: number;
}

export interface CreateEventInput {
  familyId: string;
  title: string;
  slug?: string;
  description?: string | null;
  subtitle?: string | null;
  motto?: string | null;
  yearsMilestone?: string | null;
  startDate: string;
  endDate?: string | null;
  location?: string | null;
  venue?: string | null;
  address?: string | null;
  hotelUrl?: string | null;
  rsvpDeadline?: string | null;
  itinerary?: ItineraryItem[] | null;
  faq?: FaqItem[] | null;
  published?: boolean;
}

export const eventsService = {
  async getUpcomingEvents(params?: GetUpcomingEventsParams): Promise<UpcomingEventsResponse> {
    const searchParams = new URLSearchParams();

    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.familyId) searchParams.set('familyId', params.familyId);

    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    const response = await api.get<ApiResponse<UpcomingEventsResponse>>(`/events/upcoming${query}`);
    return response.data!;
  },

  async getEventById(id: string): Promise<EventResponse> {
    const response = await api.get<ApiResponse<EventResponse>>(`/events/${id}`);
    return response.data!;
  },

  async updateEvent(id: string, data: UpdateEventInput): Promise<EventResponse> {
    const response = await api.put<ApiResponse<EventResponse>>(`/events/${id}`, data);
    return response.data!;
  },

  async listEvents(): Promise<EventsListResponse> {
    const response = await api.get<ApiResponse<EventsListResponse>>('/events');
    return response.data!;
  },

  async createEvent(data: CreateEventInput): Promise<EventResponse> {
    const response = await api.post<ApiResponse<EventResponse>>('/events', data);
    return response.data!;
  },

  async deleteEvent(id: string): Promise<void> {
    await api.delete<ApiResponse<{ id: string }>>(`/events/${id}`);
  },
};
